import * as Sentry from '@sentry/react';
import { loggingConfig } from '../config/logging.js';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogPayload {
  event?: string;
  [key: string]: unknown;
}

interface BaseContext {
  level: LogLevel;
  event?: string;
  requestId?: string;
  traceId?: string;
}

const levelPriority: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const shouldLog = (level: LogLevel) => levelPriority[level] >= levelPriority[loggingConfig.level];

const redact = (input: unknown): unknown => {
  if (input === null || input === undefined) return input;
  if (Array.isArray(input)) {
    return input.map((value) => redact(value));
  }

  if (typeof input !== 'object') {
    return input;
  }

  const fields = new Set(loggingConfig.redactFields);
  const output: Record<string, unknown> = {};

  for (const [key, rawValue] of Object.entries(input)) {
    const normalized = key.toLowerCase();
    if (fields.has(normalized)) {
      output[key] = '[redacted]';
      continue;
    }
    output[key] = redact(rawValue);
  }

  return output;
};

const emit = (level: LogLevel, message: string, payload?: LogPayload) => {
  if (!shouldLog(level)) return;

  const safePayload = redact({
    ts: new Date().toISOString(),
    service: loggingConfig.service,
    env: loggingConfig.env,
    level,
    message,
    ...payload,
  });

  const text = JSON.stringify(safePayload);
  if (level === 'error') {
    console.error(text);
  } else if (level === 'warn') {
    console.warn(text);
  } else if (level === 'info') {
    console.info(text);
  } else {
    console.debug(text);
  }

  if (!loggingConfig.enableSentry) {
    return;
  }

  const event = payload?.event ? `${payload.event}` : 'frontend.log';
  if (level === 'error' || level === 'warn') {
    Sentry.addBreadcrumb({
      level: level === 'error' ? 'error' : 'warning',
      category: 'logging',
      message,
      data: { ...(typeof safePayload === 'object' && safePayload ? safePayload : {}) },
    });
  }

  if (level === 'error' && payload && payload.error instanceof Error) {
    Sentry.captureException(payload.error, {
      tags: {
        event,
        env: loggingConfig.env,
      },
      level: 'error',
      extra: payload as Record<string, unknown>,
    });
  }
};

class Logger {
  private readonly base: BaseContext;

  constructor(base: BaseContext = { level: loggingConfig.level }) {
    this.base = base;
  }

  child(bindings: BaseContext) {
    return new Logger({
      ...this.base,
      ...bindings,
    });
  }

  debug(message: string, payload?: LogPayload) {
    emit('debug', message, { ...this.base, ...payload });
  }

  info(message: string, payload?: LogPayload) {
    emit('info', message, { ...this.base, ...payload });
  }

  warn(message: string, payload?: LogPayload) {
    emit('warn', message, { ...this.base, ...payload });
  }

  error(message: string, payload?: LogPayload) {
    emit('error', message, { ...this.base, ...payload });
  }
}

export const logger = new Logger({ level: loggingConfig.level });
